/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useOnClickOutside } from 'src/core/hooks/useOnClickOutside';
import { copyKey, selectAllKey } from 'src/core/utils/keyboardUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { tokenToCharIndices } from 'src/core/utils/labelingUtils';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { contextualMenuClassName } from 'src/modules/labeling/utils/labelingConstants';

/**
 * Takes in the labeling actions and produces event listeners that
 * can be later attached to each token.
 */
export const useTokenLabelingEventListeners = (tokenSelectionStore: TokenSelectionStore) => {
    const handleMouseDown = React.useCallback((index: number) => tokenSelectionStore.label(index), [tokenSelectionStore]);

    const handleMouseOver = React.useCallback(
        (index: number) => {
            if (tokenSelectionStore.isDragging) {
                tokenSelectionStore.label(index);
            } else {
                tokenSelectionStore.hover(index);
            }
        },
        [tokenSelectionStore]
    );

    const handleMouseUp = React.useCallback(
        (e: React.MouseEvent<HTMLSpanElement>) => {
            e.stopPropagation();
            tokenSelectionStore.stopDragging();
        },
        [tokenSelectionStore]
    );

    const handleMouseLeave = React.useCallback(() => tokenSelectionStore.unHover(), [tokenSelectionStore]);

    const handleKeyboard = React.useCallback(
        (e: React.KeyboardEvent<HTMLSpanElement>, index: number) => {
            if (e.key === LuisKeyCodes.Enter) {
                tokenSelectionStore.label(index, true);

                return;
            }

            if ((e.key === LuisKeyCodes.ArrowRight || e.key === LuisKeyCodes.ArrowLeft) && !e.shiftKey) {
                tokenSelectionStore.hover(index);

                return;
            }

            if (e.key === LuisKeyCodes.ArrowRight) {
                tokenSelectionStore.labelRight(index);
            } else if (e.key === LuisKeyCodes.ArrowLeft) {
                tokenSelectionStore.labelLeft(index);
            }
        },
        [tokenSelectionStore]
    );

    return React.useCallback(
        (index: number) => ({
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseLeave,
            onMouseDown: () => handleMouseDown(index),
            onMouseEnter: () => handleMouseOver(index),
            onKeyUp: (e: React.KeyboardEvent<HTMLSpanElement>) => handleKeyboard(e, index)
        }),
        [handleMouseDown, handleMouseOver, handleMouseUp, handleMouseLeave, handleKeyboard]
    );
};

/**
 * Registers global listeners for canceling the labeling.
 */
export const useGlobalLabelingEventListeners = <T extends HTMLElement>({
    ref,
    text,
    onRemoveRange,
    tokenizedText,
    tokenSelectionStore,
    isCharacterTokenization
}: {
    text: string;
    tokenizedText: string[];
    ref: React.MutableRefObject<T>;
    isCharacterTokenization: boolean;
    tokenSelectionStore: TokenSelectionStore;
    onRemoveRange?: (start: number, end: number) => void;
}) => {
    useDocumentEventHandler(
        'keyup',
        React.useCallback(
            async (e: KeyboardEvent) => {
                if (e.key === LuisKeyCodes.Escape) {
                    tokenSelectionStore.cancel();
                }

                if (tokenSelectionStore.selection[0] === -1) {
                    return;
                }

                if (e.key === 'Delete') {
                    e.preventDefault();
                    onRemoveRange?.(tokenSelectionStore.selection[0], tokenSelectionStore.selection[1]);
                    tokenSelectionStore.cancel();

                    return;
                }

                if (copyKey(e) && navigator.clipboard) {
                    e.preventDefault();
                    const charIndices = tokenToCharIndices({
                        text,
                        tokenizedText,
                        isCharacterTokenization,
                        endTokenIndex: tokenSelectionStore.selection[1],
                        startTokenIndex: tokenSelectionStore.selection[0]
                    });
                    await navigator.clipboard.writeText(text.slice(charIndices.startCharIndex, charIndices.endCharIndex + 1));
                }

                if (selectAllKey(e)) {
                    e.preventDefault();
                    tokenSelectionStore.selectAll(tokenizedText.length - 1);
                }
            },
            [tokenSelectionStore, tokenizedText, text, tokenSelectionStore, onRemoveRange]
        )
    );

    useOnClickOutside(
        ref,
        React.useCallback(() => tokenSelectionStore.cancel(), [tokenSelectionStore]),
        React.useCallback(targetElement => {
            const menuContainers = document.querySelectorAll(contextualMenuClassName);

            return [...menuContainers].some(container => container.contains(targetElement));
        }, [])
    );
};
